import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import { Button, Icon, Badge } from '@pwc/pwc-deals-ui';
import { useMobile } from '@pwc/pwc-deals-web-common';
import { useDispatch, useSelector } from 'react-redux';

import useLock from 'common/hooks/useLock';
import { useMobileContentHighlight } from 'common/hooks/useMobileContentHighlight';
import useGlobalError from 'common/components/GlobalError/useGlobalError';
import {
  getCommentPanelOpened,
  getViewingStatus,
  setCommentPanelOpened,
  viewUnresolvedComments,
  getRibbonPanelItem,
  getComments,
} from 'features/comments/commentsSlice';

import { useCommentCounts } from 'common/hooks/useCommentCounts';
import ActionsWithLock from '../../../common/components/ActionsWithLock/ActionsWithLock';

import { reportPreviewPath } from '../../../common/services/urlService';
import styles from './AnalysisControlsHeader.module.scss';
import './AnalysisControlsHeader.scss';
import { COMMENT_VIEWING_STATUS } from '../../comments/constants';
import { getIsDarkModeSelector } from '../../../common/redux/userSettings/userSettingsSlice';

const AnalysisControlsHeaderError = ({ name }) => {
  useGlobalError({
    containerId: '.global-error-placeholder',
    metadata: {
      bannerMetadata: { alertLevel: 'info', bannerPosition: 'bottom' },
    },
    userName: name,
  });

  return (
    <div className={styles['analysis-control-header__error']}>
      <div className="global-error-placeholder" />
    </div>
  );
};

const AnalysisControlsHeader = ({
  analysis,
  children,
  disableEdit = false,
  disableMoreMenu = false,
  hideEdit = false,
  isInternalUser,
  onDelete,
  onEdit,
  onRefresh,
  rightAlignItemsToright = false,
  viewClient,
  workflowActions = [],
  commentingAutoLockContentId = '',
}) => {
  const dispatch = useDispatch();
  const viewingStatus = useSelector(getViewingStatus);
  const commentPanelOpened = useSelector(getCommentPanelOpened);
  const darkMode = useSelector(getIsDarkModeSelector);
  const isMobile = useMobile();
  const ribbonPanelItemType = useSelector(getRibbonPanelItem);

  const isPanelOpened = useMemo(
    () => ribbonPanelItemType.type != null,
    [ribbonPanelItemType.type]
  );
  const { isEntitySharedWithClient } = useSelector(getComments);
  const { visibleCommentCounts } = useCommentCounts({
    isShared: isEntitySharedWithClient,
    viewingStatus,
    isPanelOpened,
  });

  const visibleCommentCountsText =
    visibleCommentCounts > 9 ? '9+' : visibleCommentCounts;
  const { projectId } = useParams();
  const reportPreviewUrl = reportPreviewPath(
    projectId,
    analysis.contentThreadId,
    viewClient.isOnClientVersion ? 'shared' : 'latest',
    analysis.isUnStructured
  );

  const { name } = useLock({ contentThreadId: analysis.contentThreadId });

  useMobileContentHighlight({ commentPanelOpened, isMobile });

  const handlePanelButtonClick = () => {
    if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
      dispatch(viewUnresolvedComments());
    }
    dispatch(setCommentPanelOpened(!commentPanelOpened));
  };

  return (
    <div
      className={classnames(
        styles['analysis-control-header'],
        !isInternalUser
          ? styles['current-user-is-client']
          : styles['current-user-is-pwc'],
        {
          [styles.rightAlignFlexItemsToright]: rightAlignItemsToright,
        }
      )}
    >
      {children && (
        <div className={styles['analysis-control-header__header']}>
          {children}
        </div>
      )}

      <div className={styles['analysis-control-header__sub-header']}>
        <ActionsWithLock
          buttonOnEdit={onEdit}
          disableMoreMenu={disableMoreMenu}
          hideEdit={hideEdit}
          isEditDisabled={disableEdit || viewClient.isOnClientVersion}
          itemId={analysis.contentId}
          onDelete={onDelete}
          onRefresh={onRefresh}
          reportPreviewUrl={reportPreviewUrl}
          viewClient={viewClient}
          workflowActions={workflowActions}
          contentThreadId={commentingAutoLockContentId}
        />
        <span>
          {isMobile && visibleCommentCounts > 0 && (
            <Badge
              cssClass={styles.counter}
              show
              text={visibleCommentCountsText}
            />
          )}
          <Button
            className={styles['mobile-panel-button']}
            aria-label="mobile-panel-button icon"
            onClick={handlePanelButtonClick}
            data-testid="panel-button"
          >
            {darkMode ? (
              <Icon name="tooltip-open-darkMode" width="34" height="32" />
            ) : (
              <Icon name="tooltip-open-lightMode" width="34" height="32" />
            )}
          </Button>
        </span>
      </div>
      {name && <AnalysisControlsHeaderError name={name} />}
    </div>
  );
};

AnalysisControlsHeader.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
};

AnalysisControlsHeader.defaultProps = {
  onRefresh: undefined,
};

export default AnalysisControlsHeader;
